import { useMemo, useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowUp,
  BookOpen,
  CircleQuestionMark,
  FileUp,
  RefreshCw,
  Video,
  WandSparkles,
  type LucideIcon,
} from 'lucide-react';
import { useTheme } from '@course-authoring/shared-types';
import { adminText } from '../../admin-ui';
import { useClientContext } from '../../hooks/useClientContext';
import { uploadDocument, type CreateBuildSource } from '../../api/authorWithAiApi';
import { useCreateBuild } from '../../api/authorWithAiQueries';
import { DOCUMENT_ACCEPT, DOCUMENT_MIME_TYPES } from '../../utils/documentExtraction/types';
import { BuildLists } from '../../components/authorWithAi/BuildLists';
import {
  ASK_EXAMPLES,
  ENTRY_POINT_AVAILABILITY,
  EXPERTISE_CARDS,
  LANDING_COPY,
  NOT_AVAILABLE_LABEL,
} from '../../components/authorWithAi/authorWithAiCopy';
import { createAuthorWithAiStyles } from '../../components/authorWithAi/authorWithAiStyles';
import { AuthorWithAiGate } from './AuthorWithAiGate';

const CARD_ICONS = { question: CircleQuestionMark, video: Video, customize: BookOpen } as const satisfies Record<string, LucideIcon>;

const OUTLINE = { size: '1em', strokeWidth: 1.75, 'aria-hidden': true } as const;

export function AuthorWithAiLandingPage() {
  return <AuthorWithAiGate>{(readiness) => <Landing ready={readiness.ready} missingSettings={readiness.missingSettings} uploadsReady={readiness.uploadsReady} />}</AuthorWithAiGate>;
}

function Landing({ ready, missingSettings, uploadsReady }: { ready: boolean; missingSettings: string[]; uploadsReady: boolean }) {
  const { tokens } = useTheme();
  const styles = useMemo(() => createAuthorWithAiStyles(tokens), [tokens]);
  const navigate = useNavigate();
  const { clientContext } = useClientContext();
  const accountId = clientContext ? Number(clientContext.id) : null;
  const create = useCreateBuild();
  const [text, setText] = useState('');
  const [askFocused, setAskFocused] = useState(false);
  const [exampleIndex, setExampleIndex] = useState(0);
  const [askError, setAskError] = useState<string | null>(null);
  const [uploadStatus, setUploadStatus] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const creating = useRef(false);
  const example = ASK_EXAMPLES[exampleIndex % ASK_EXAMPLES.length]!;
  // The chip needs the server's private upload bucket as well as the feature itself.
  const uploadAvailable = ENTRY_POINT_AVAILABILITY.upload && uploadsReady;

  const start = (source: CreateBuildSource, initialMessage?: string) => {
    // One build per entry: a second pick or click while creating is ignored.
    if (creating.current) return;
    creating.current = true;
    setAskError(null);
    create.mutate({ accountId, source, ...(initialMessage ? { initialMessage } : {}) }, {
      onSettled: () => { creating.current = false; },
      onSuccess: (response) => navigate(`/author-with-ai/builds/${response.buildId}`),
      onError: (error) => setAskError(error instanceof Error ? error.message : 'Could not start the build.'),
    });
  };

  const submitAsk = (event?: FormEvent) => {
    event?.preventDefault();
    const value = text.trim();
    if (!value || create.isPending) {
      inputRef.current?.focus();
      return;
    }
    start({ kind: 'none' }, value);
  };

  // Both cards start a conversation: "Ask a question" narrows by what to
  // measure, "Lead with a video scenario" asks what moment to show.
  const openCard = (id: 'question' | 'video' | 'customize') => {
    if (id === 'customize' || !ENTRY_POINT_AVAILABILITY[id]) return;
    start({ kind: id === 'question' ? 'question_browse' : 'video_browse' });
  };

  // "Upload a file": the browser reads the file into sections (the extractor
  // loads on first use, it carries pdf.js), the original goes to the API,
  // and the build opens on the file message. A refusal or failure shows
  // under the chip and nothing else happens.
  const startUpload = async (file: File) => {
    if (creating.current) return;
    creating.current = true;
    setAskError(null);
    setUploadError(null);
    try {
      setUploadStatus(LANDING_COPY.uploadReading(file.name));
      const { extractDocument } = await import('../../utils/documentExtraction');
      const extracted = await extractDocument(file);
      setUploadStatus(LANDING_COPY.uploadUploading(file.name));
      const { fileId } = await uploadDocument(file, DOCUMENT_MIME_TYPES[extracted.documentType]);
      const response = await create.mutateAsync({ accountId, source: { kind: 'uploaded_file', fileId }, document: { sections: extracted.sections } });
      navigate(`/author-with-ai/builds/${response.buildId}`);
    } catch (error) {
      setUploadError(error instanceof Error && error.message ? error.message : LANDING_COPY.uploadFailed);
    } finally {
      creating.current = false;
      setUploadStatus(null);
    }
  };

  const onFileChosen = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (file) void startUpload(file);
  };

  return (
    <div style={styles.page}>
      <h1 style={{ ...adminText.pageTitle, fontSize: '36px', fontWeight: 600, textAlign: 'center', margin: '24px 0 20px' }}>{LANDING_COPY.heading}</h1>
      {!ready && <div style={styles.banner} role="status">{LANDING_COPY.notReady(missingSettings)}</div>}

      <div style={styles.groups}>
        <section style={styles.group} aria-labelledby="awa-expertise">
          <h2 id="awa-expertise" style={styles.groupHeading}>{LANDING_COPY.expertiseHeading}</h2>
          <div style={styles.cards}>
            {EXPERTISE_CARDS.map((card) => {
              const available = ENTRY_POINT_AVAILABILITY[card.id];
              return (
                <button
                  key={card.id}
                  type="button"
                  style={{ ...styles.card, ...(available ? {} : styles.cardDisabled) }}
                  onClick={() => openCard(card.id)}
                  disabled={!available || create.isPending}
                  aria-disabled={!available}
                  title={available ? undefined : NOT_AVAILABLE_LABEL}
                >
                  <span style={styles.cardIcon} aria-hidden="true">{(() => { const Icon = CARD_ICONS[card.id]; return <Icon {...OUTLINE} />; })()}</span>
                  <h3 style={styles.cardTitle}>{card.title}</h3>
                  <p style={styles.cardBody}>{card.body}</p>
                  {!available && <span style={styles.notAvailable}>{NOT_AVAILABLE_LABEL}</span>}
                </button>
              );
            })}
          </div>
        </section>

        <div style={styles.orBar} aria-hidden="true">
          <span style={styles.orLine} />
          <span>{LANDING_COPY.or}</span>
          <span style={styles.orLine} />
        </div>

        <section style={styles.group} aria-labelledby="awa-own">
          <h2 id="awa-own" style={styles.groupHeading}>{LANDING_COPY.ownHeading}</h2>
          <form style={{ ...styles.askBar, ...(askFocused ? styles.barFocused : {}) }} onSubmit={submitAsk}>
            <span style={styles.spark} aria-hidden="true"><WandSparkles {...OUTLINE} /></span>
            <span style={styles.askField}>
              <input
                ref={inputRef}
                style={styles.askInput}
                value={text}
                onChange={(event) => setText(event.target.value)}
                onFocus={() => setAskFocused(true)}
                onBlur={() => setAskFocused(false)}
                aria-label={LANDING_COPY.askLabel}
                disabled={create.isPending}
              />
              {text.length === 0 && (
                <span style={styles.askGhost}>
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '15px' }} aria-hidden="true">{LANDING_COPY.askPlaceholder}</span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: tokens.spacing.sm, flexShrink: 1, minWidth: 0 }}>
                    <span aria-hidden="true">{LANDING_COPY.examplePrefix}</span>
                    <button
                      type="button"
                      style={styles.askExample}
                      onClick={() => start({ kind: 'none' }, example)}
                      disabled={create.isPending}
                    >
                      {example}
                    </button>
                    <button
                      type="button"
                      style={{ ...styles.iconButton, width: '22px', height: '22px', pointerEvents: 'auto', fontSize: '11px' }}
                      onClick={() => setExampleIndex((index) => (index + 1) % ASK_EXAMPLES.length)}
                      aria-label={LANDING_COPY.anotherExample}
                    >
                      <RefreshCw {...OUTLINE} />
                    </button>
                  </span>
                </span>
              )}
            </span>
            {text.trim() && (
              <button
                type="submit"
                style={{ ...styles.iconButton, ...styles.iconButtonActive }}
                aria-label={LANDING_COPY.send}
                disabled={create.isPending}
              >
                <ArrowUp {...OUTLINE} />
              </button>
            )}
          </form>
          {askError && <p style={styles.error} role="alert">{askError}</p>}
          <div style={styles.altRow}>
            <span style={styles.muted}>{LANDING_COPY.or}</span>
            <button
              type="button"
              style={{ ...styles.chip, ...(uploadAvailable ? {} : styles.buttonDisabled) }}
              disabled={!uploadAvailable || create.isPending || uploadStatus !== null}
              title={uploadAvailable ? undefined : NOT_AVAILABLE_LABEL}
              onClick={() => fileInputRef.current?.click()}
            >
              <FileUp {...OUTLINE} />
              {LANDING_COPY.uploadChip}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept={DOCUMENT_ACCEPT}
              style={{ display: 'none' }}
              data-testid="upload-input"
              onChange={onFileChosen}
            />
            {uploadStatus
              ? <span style={styles.muted} role="status">{uploadStatus}</span>
              : <span style={styles.muted}>{LANDING_COPY.uploadNote}</span>}
            {!uploadAvailable && <span style={styles.notAvailable}>{NOT_AVAILABLE_LABEL}</span>}
          </div>
          {uploadError && <p style={styles.error} role="alert">{uploadError}</p>}
        </section>
      </div>

      <BuildLists accountId={accountId} />
    </div>
  );
}
